import { useMemo } from 'react';
import { useQuery } from '@tanstack/react-query';
import { accountQuery, cmsPagesQuery, hostBaseUrl } from '@/api';
import {
  FOOTER_POLICY_LINKS,
  footerSectionsFromPages,
  socialMediaFromPages,
  type FooterCmsLink,
  type FooterCmsSection,
  type FooterSocialLink,
} from '@/domain';
import { useTranslations } from '@/i18n/TranslationsProvider';

export interface FooterCmsContent {
  sections: FooterCmsSection[];
  socialLinks: FooterSocialLink[];
  policyLinks: FooterCmsLink[];
}

export function useFooterPages(): FooterCmsContent {
  const { data: account } = useQuery(accountQuery());
  const { selectedLanguage } = useTranslations();
  const accountGuid = account?.Guid ?? '';

  const { data } = useQuery({
    ...cmsPagesQuery(accountGuid, selectedLanguage),
    enabled: Boolean(accountGuid && selectedLanguage),
  });

  return useMemo(() => {
    if (!accountGuid) {
      return { sections: [], socialLinks: [], policyLinks: FOOTER_POLICY_LINKS };
    }

    const pages = data?.Data ?? [];

    return {
      sections: footerSectionsFromPages(pages, {
        allowUserRegistration: account?.AllowUserRegistration !== false,
        allowDeposits: account?.AllowDeposits !== false,
      }),
      socialLinks: socialMediaFromPages(pages, hostBaseUrl('users'), accountGuid),
      policyLinks: FOOTER_POLICY_LINKS,
    };
  }, [account?.AllowDeposits, account?.AllowUserRegistration, accountGuid, data]);
}
